import {
  buildGuide,
  paragraphs,
  bullets,
  steps,
  callout,
  workflow,
  faq,
  table,
  fieldTable,
  permissionTable,
  heading,
  video,
} from '../helpers';

export const dashboardGuides = [
  buildGuide({
    id: 'dashboard-home',
    title: 'Home Dashboard',
    moduleId: 'dashboard',
    summary:
      'Your daily operations board: booking calendar, arrivals context, quick booking, and pending tasks.',
    keywords: ['dashboard', 'calendar', 'home', 'quick book', 'tasks', 'arrivals'],
    pathHint: 'Hotel → Dashboard',
    related: ['quick-booking', 'reservation', 'walk-in', 'check-in'],
    overview: [
      ...paragraphs(
        'The Home Dashboard is the first screen most front-office staff open each shift. It shows a room-by-date booking calendar so you can see occupancy at a glance, start bookings from an available cell, and clear operational follow-ups from the Pending Tasks rail.',
        'Use it to answer floor questions in seconds: Which rooms are free tonight? Who arrives today? Which checkouts or cleanings are still pending? Managers also use it mid-day to rebalance allocations when a VIP upgrades or a room goes out of order.',
        'Unlike Performance Dashboard or Booking Dashboard, Home Dashboard is operational — not an analytics report. Charts belong elsewhere; this screen is for selling and servicing rooms today.',
        'Example: a guest calls for a one-night stay. Reception opens Dashboard, checks the date column, clicks an available cell to launch Quick Booking, saves the stay, then watches the calendar block appear so nobody double-sells that room.',
        'Benefits: fewer overbooks, faster walk-ins, shared visibility between FO and supervisors, and a single place to start the morning without hunting through Booking List first.'
      ),
    ],
    beforeYouBegin: [
      permissionTable([
        ['Hotel context open', 'You must open a hotel from Hotelier first'],
        [
          'common.manage_booking_operations',
          'Needed to create bookings from the calendar / Quick Book',
        ],
        [
          'Rooms configured',
          'Empty calendars usually mean Configuration → Rooms is incomplete',
        ],
      ]),
      bullets(
        [
          'Correct hotel selected (confirm header name)',
          'At least one active room and rate plan for sell dates',
          'Agree with HK how you will use Pending Tasks vs Housekeeping board',
        ],
        'Prerequisites'
      ),
      callout(
        'warning',
        'Date column discipline',
        'It is easy to click yesterday or tomorrow when the grid is scrolled. Always read the date header before saving a Quick Booking.'
      ),
    ],
    steps: [
      steps([
        {
          text: 'Open Dashboard from the main hotel navigation after selecting your property on Hotelier.',
        },
        {
          text: 'Confirm the hotel name in the header. Scan the calendar for today’s date column and occupied vs available cells.',
        },
        {
          text: 'Use date navigation / day-count controls to move across the grid when planning arrivals for the next few days.',
        },
        {
          text: 'Review the Pending Tasks rail or panel (checkouts, check-ins, no-shows, housekeeping cleanings/maintenance). Open a task to jump into the related workflow.',
        },
        {
          text: 'For a phone booking or hold, click an available room/date cell (or Quick Book) to open the Quick Booking drawer with context pre-filled.',
        },
        {
          text: 'Complete guest and stay details in the drawer, then Save. The calendar refreshes with a new booking block on those nights.',
        },
        {
          text: 'If the guest is present and needs full ID capture, prefer Walk-in instead of a minimal quick book — still verify the room remains free on the grid.',
        },
        {
          text: 'During the shift, re-check Pending Tasks after peak check-in/out so lingering cleanings or late checkouts are not forgotten.',
        },
        {
          text: 'Do not leave unfinished Quick Booking drafts open on shared PCs — complete Save or close the drawer.',
        },
      ]),
    ],
    screens: [
      bullets(
        [
          'Calendar grid — rooms (rows) vs dates (columns) with booking blocks',
          'Date / range controls — move the visible window and day count',
          'Quick Booking drawer — create a booking without leaving Dashboard',
          'Pending Tasks rail/panel — check-in, checkout, no-show, housekeeping items',
          'Header hotel name — always confirm before acting',
        ],
        'Primary UI elements'
      ),
      table(
        ['Element', 'What it tells you', 'Typical action'],
        [
          ['Empty/available cell', 'Room free that night', 'Quick Book or hold for walk-in'],
          ['Booking block', 'Stay already sold', 'Open booking detail / avoid double sell'],
          ['Pending check-in task', 'Arrival still needs check-in', 'Start Check-in workflow'],
          ['Pending checkout task', 'Departure due / overdue', 'Start Checkout & settlement'],
          ['HK cleaning task', 'Room not ready', 'Coordinate with Housekeeping'],
        ],
        'Reading the board'
      ),
    ],
    workflow: [
      workflow('Morning shift start', [
        'Open hotel',
        'Open Dashboard',
        'Confirm header / date',
        'Clear Pending Tasks priorities',
        'Review occupancy for tonight',
        'Prepare arrival list',
        'Handle walk-ins via Quick Book or Walk-in',
        'Recheck tasks after peak',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Calendar cells reflect live booking and room availability for the selected hotel. Creating a booking from a cell pre-fills date and room context to reduce wrong-night and wrong-room errors.',
        'Pending Tasks are derived from operational state: arrivals in confirmed/reserved/pending (and partial check-in) statuses, checkouts due, no-shows, open housekeeping cleanings, and maintenance items. Completing the underlying workflow (check-in, checkout, finish cleaning) updates or clears the task — you usually do not “delete” a task on its own.',
        'Quick Booking creates a real booking record under the same availability and pricing rules as Reservation/Walk-in. After Save, inventory for those dates is held according to booking status rules and the block appears on the grid for other staff.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Use the calendar before promising a room',
        'When a guest calls, check the Dashboard date range first so you never double-sell.'
      ),
      callout(
        'tip',
        'Tasks before tea',
        'Clear high-priority check-ins and overdue checkouts before personal breaks so guests are not waiting at the desk.'
      ),
      callout(
        'note',
        'Analytics live elsewhere',
        'For revenue/occupancy trends use Performance Dashboard; for booking KPIs and PDF snapshots use Booking Dashboard.'
      ),
    ],
    bestPractices: [
      bullets([
        'Start every shift on Dashboard for two minutes before other modules.',
        'Do not leave Quick Booking drawers unfinished — complete or discard.',
        'Call out OOOs and VIP holds verbally and on the board so the next agent sees them.',
        'After a no-show decision, update booking status so tasks and inventory stay truthful.',
        'On mobile/tablet, use the Tasks-focused view when you are away from the full calendar.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Booking on the wrong date column after scrolling the grid.',
        'Ignoring Pending Tasks until guests are waiting at the desk.',
        'Promising a room that still shows a dirty/pending cleaning task.',
        'Confusing Home Dashboard with Performance Dashboard in owner meetings.',
        'Creating overlapping stays because the calendar was not refreshed after a colleague’s save.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'Fix'],
        [
          [
            'Empty calendar',
            'No rooms configured or wrong hotel',
            'Confirm hotel header; create Rooms under Configuration',
          ],
          [
            'Cannot Quick Book',
            'Missing manage_booking_operations or date not allowed',
            'Check role permission; use an eligible check-in date',
          ],
          [
            'Pending Tasks empty but work exists',
            'Cache/stale bundle or filters/day mismatch',
            'Refresh Dashboard; complete status in source module if needed',
          ],
          [
            'Booking block missing after Save',
            'Save failed or different date window visible',
            'Re-open Booking List; navigate calendar to the stay dates',
          ],
          [
            'Double enquiry on same room',
            'Two agents booking without checking grid',
            'Agree one owner for phone holds; refresh before Save',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Is Dashboard the same as Performance Dashboard?',
          a: 'No. Home Dashboard is operational (calendar + tasks). Performance Dashboard is analytics (revenue, occupancy trends).',
        },
        {
          q: 'Can I edit an existing booking from the calendar?',
          a: 'Open the booking block / Booking List / Booking Detail to edit, cancel, or add extras. Quick Book is for creating new stays.',
        },
        {
          q: 'Why do some dates refuse Quick Book?',
          a: 'Quick Book enforces allowed check-in date rules (for example past dates may be blocked). Use the proper booking flow your policy allows, or pick a valid date.',
        },
        {
          q: 'Are Pending Tasks the same as Housekeeping?',
          a: 'No. Housekeeping has its own boards. Pending Tasks surface cross-operational reminders including some HK cleanings/maintenance for FO visibility.',
        },
        {
          q: 'Does the calendar include OTA bookings?',
          a: 'Yes — once channel/NexaStay bookings sync into the PMS they appear as booking blocks like direct stays.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'quick-booking',
    title: 'Quick Booking',
    moduleId: 'dashboard',
    summary: 'Create a booking fast from the Dashboard calendar cell or Quick Book action.',
    keywords: ['quick booking', 'quick book', 'calendar booking', 'drawer'],
    pathHint: 'Dashboard → Quick Booking drawer',
    related: ['dashboard-home', 'walk-in', 'reservation', 'room-allocation'],
    overview: [
      ...paragraphs(
        'Quick Booking is the speed path at the front desk. It opens a drawer pre-filled from the calendar cell (room and date context) so you can capture a short stay without navigating to the full Reservation page.',
        'Use it for phone holds, simple FIT stays, and same-day enquiries when the guest details are light. Prefer Walk-in when the guest is physically present and you need the fuller ID/document capture flow your property requires.',
        'The drawer walks through stay dates, room selection, meal plan and night rate, guest details, optional company/tax options, discounts/extras, and payment collection fields before Save.',
        'Example: a corporate guest calls for two nights in a Deluxe. FO clicks the Deluxe cell on the arrival date, confirms nights and meal plan, enters name and mobile, takes a partial advance in cash, Saves, then later assigns/upgrades from Booking Detail if needed.',
        'After a successful save, the calendar shows the new block, inventory for those nights is held per status rules, and you can continue to room assignment, check-in, invoice, or payment-link actions from the success path when offered.'
      ),
    ],
    beforeYouBegin: [
      permissionTable([
        [
          'common.manage_booking_operations',
          'Required to create bookings from Quick Book',
        ],
        [
          'Rates & rooms ready',
          'Room types, rooms, rate plans, and Manage Rate should exist for the dates',
        ],
      ]),
      bullets(
        [
          'Guest name and reachable mobile number',
          'Agreed check-in date, nights, and room type',
          'Advance amount and payment method if collecting now',
          'Know whether Walk-in is more appropriate for ID-heavy arrivals',
        ],
        'Prerequisites'
      ),
      callout(
        'warning',
        'Read back before Save',
        'Wrong nights and wrong mobile numbers are the top Quick Book defects. Confirm both aloud with the guest.'
      ),
    ],
    steps: [
      steps([
        {
          text: 'On Dashboard, click an available room/date cell or the Quick Book control. Confirm the pre-filled check-in context matches what the guest asked for.',
        },
        {
          text: 'Set check-in date and length of stay (nights). Watch the quoted nights update before continuing.',
        },
        {
          text: 'Select room(s) / room type as prompted. Review availability for the range; pick another room if the cell conflicted.',
        },
        {
          text: 'Choose meal plan when offered. Review the night rate amount (auto-filled from PMS rates; editable when your process allows).',
        },
        {
          text: 'Enter guest details: title, name fields, phone (with country code), email if available, and notes/ETA for late arrivals.',
        },
        {
          text: 'Add document details if required now, or continue and capture IDs at check-in per property SOP. Attach company/GST details when the stay is billed to a company.',
        },
        {
          text: 'Configure tax-inclusive options, discounts, and extra charges only with authorised amounts. Add a short note explaining extras.',
        },
        {
          text: 'Set payment type (for example full/partial), method (cash/card/etc.), amount, reference number if non-cash, and collection date.',
        },
        {
          text: 'Save/confirm. Wait for success — the calendar updates with the booking block. Use success actions (invoice/email/payment link) when shown and permitted.',
        },
        {
          text: 'If the guest has arrived, continue to room assignment and Check-in. If not, ensure remarks cover late arrival or special requests for the next shift.',
        },
      ]),
    ],
    screens: [
      heading('Stay & inventory'),
      fieldTable([
        ['Check-in date', 'Yes', 'First night / arrival date for the stay'],
        ['Nights / check-out', 'Yes', 'Length of stay; drives inventory hold'],
        ['Room / room type', 'Yes', 'Which inventory units to reserve'],
        ['Meal plan', 'When configured', 'Selects rate basis (for example EP/CP/MAP)'],
        ['Night rate amount', 'Yes for priced stays', 'Per-night tariff; often auto-filled from Manage Rate'],
      ]),
      heading('Guest & commercial'),
      fieldTable([
        ['Guest name fields', 'Yes', 'Primary guest identity on the booking'],
        ['Mobile + country code', 'Usually yes', 'Contact key and future search'],
        ['Email', 'Recommended', 'Confirmations and e-invoice delivery'],
        ['ETA / guest note / remarks', 'No', 'Late arrival and shift handover context'],
        ['Documents', 'Per SOP', 'ID type/number/images when captured now'],
        ['Company details', 'If corporate', 'GST/billing entity for the folio'],
        ['Discount / extras', 'Optional', 'Authorised adjustments with notes'],
        ['Payment fields', 'If collecting', 'Type, method, amount, reference, collection date'],
      ]),
      bullets(
        [
          'Drawer steps/progress — move through stay → guest → money without leaving Dashboard',
          'Success dialog — booking created; optional invoice, email, or payment-link actions',
          'Close / cancel — discards an unfinished draft (does not create inventory hold)',
        ],
        'Drawer controls'
      ),
    ],
    workflow: [
      workflow('Quick book path', [
        'Select calendar cell',
        'Confirm dates & room',
        'Set meal plan / rate',
        'Enter guest + contact',
        'Apply tax/discount/extras if needed',
        'Collect advance if any',
        'Save booking',
        'Assign room / check-in if present',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Quick Booking creates a real booking record subject to the same availability rules as Reservation and Walk-in. When you confirm, inventory for those dates is reduced/held according to the booking’s status rules and the grid block becomes visible to other users.',
        'Rates typically come from meal plan + Manage Rate for the night; manual night amounts may be allowed depending on your workflow, but unauthorised undercharging will show up in revenue reports and audits.',
        'Payments entered in the drawer post against the booking and feed finance/cash views. Payment links (email/WhatsApp) appear on success only when communications permissions and configuration allow them.',
        'Editing later (dates, room, cancellation, extras) is done from Booking List / Booking Detail — not by re-opening an empty Quick Book drawer.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Walk-in vs Quick Book',
        'If the guest is physically present and needs ID capture, prefer Walk-in for the full guest form and documents.'
      ),
      callout(
        'tip',
        'Late arrivals',
        'Put ETA in guest note/remarks so night staff do not mark a no-show too early.'
      ),
      callout(
        'note',
        'Company GST stays',
        'Save company details when the invoice must carry GSTIN — fixing this after checkout is harder.'
      ),
    ],
    bestPractices: [
      bullets([
        'Always read back dates, room type, and mobile before saving.',
        'Add notes for late arrivals, airport pickups, and bed preferences.',
        'Collect advances with the correct payment method so cash counter matches.',
        'Search existing guest by mobile when the drawer supports profile pick — avoid duplicate CRM noise.',
        'After Save, glance at the calendar block once to confirm nights and room.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Saving without verifying mobile number or country code.',
        'Overlapping an occupied room because the grid was stale.',
        'Applying discounts without supervisor approval.',
        'Using Quick Book for complex group allotments that need Reservation tools.',
        'Forgetting to take document IDs until the guest is already in the room against policy.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'Fix'],
        [
          [
            'No availability',
            'Room sold or dates conflict',
            'Pick another room/type or shift dates; refresh calendar',
          ],
          [
            'Rate missing / zero',
            'Manage Rate not set for plan/dates',
            'Set Manage Rate for that room type and meal plan',
          ],
          [
            'Cannot submit',
            'Required guest/stay fields invalid',
            'Complete highlighted required fields; check phone format',
          ],
          [
            'Payment link not shown',
            'Communications not configured or permission missing',
            'Use manual collection; ask admin to enable payment-link channels',
          ],
          [
            'Duplicate booking created',
            'Double-click Save or two agents',
            'Cancel the accidental booking; train single-submit habit',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Can I edit a Quick Booking later?',
          a: 'Yes — open Booking List or Booking Detail / Edit Booking for changes, cancellations, or extras.',
        },
        {
          q: 'Does Quick Book assign the room automatically?',
          a: 'It reserves inventory in context of the cell/room selection. Follow your allocation/check-in process if further assignment or key handoff is required.',
        },
        {
          q: 'Can I book multiple rooms?',
          a: 'When the drawer allows selecting multiple rooms, yes. For large groups, Reservation is usually clearer.',
        },
        {
          q: 'Are taxes calculated here?',
          a: 'Tax behaviour follows Finance Setup (tax rules, inclusive flags). Verify the quote before collecting payment.',
        },
        {
          q: 'What happens if I close the drawer mid-way?',
          a: 'Unsaved drafts are discarded. No inventory hold is created until Save succeeds.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'performance-dashboard',
    title: 'Performance Dashboard',
    moduleId: 'dashboard',
    summary: 'Property analytics for revenue, expenses, occupancy, and sales performance.',
    keywords: ['performance', 'analytics', 'revenue', 'occupancy', 'kpi'],
    pathHint: 'Hotel → Performance Dashboard',
    related: ['booking-dashboard', 'finance-dashboard', 'reports-hub'],
    videos: [
      video(
        'gfDCEBgaJJA',
        'Unlock complete hotel visibility with Roomnexa Insights',
        'See how performance analytics help managers review revenue, occupancy, and sales.'
      ),
    ],
    overview: [
      ...paragraphs(
        'Performance Dashboard is the property-level analytics board for managers and owners working inside one hotel. It visualises revenue, hotel expenses, hotel due, profit, bookings vs cancellations, occupancy, customers, payment methods, booking sources, and inventory mix.',
        'Use it for weekly business reviews, rate strategy conversations, and coaching (“OTA share spiked”, “hotel due rose while occupancy flat”). It is not the place to check in a guest — use Home Dashboard for operations.',
        'The page defaults to a date range (commonly the current month). Changing start/end dates reloads aggregated statistics for that window. PDF export captures a shareable snapshot for ownership packs.',
        'Example: GM notices occupancy healthy but profit soft. Performance shows hotel due elevated and payment mix skewed. GM pairs the chart with Reports Hub registers, then briefs FO with concrete actions for next week.',
        'For multi-hotel rollups, use Organisation Dashboard on Landing instead — Performance stays single-property.'
      ),
    ],
    beforeYouBegin: [
      permissionTable([
        [
          'Analytics / reports access',
          'Typically requires view_reports or equivalent performance access for your role',
        ],
        ['Hotel open', 'Open the property from Hotelier before viewing property KPIs'],
      ]),
      bullets(
        [
          'Know the date range you want (WTD, MTD, last month)',
          'Understand that same-day POS and late checkouts can still move numbers',
          'Have Reports Hub handy when you need line-level exports, not only charts',
        ],
        'Prerequisites'
      ),
      callout(
        'note',
        'Apples to apples',
        'Compare the same weekday pattern and date length when judging “this week vs last week”.'
      ),
    ],
    steps: [
      steps([
        {
          text: 'Open Performance Dashboard from the hotel navigation (not the Landing org dashboard).',
        },
        {
          text: 'Set start and end dates for the analysis window. Wait for metrics and charts to finish loading.',
        },
        {
          text: 'Read top KPI cards: Total Revenue, Hotel expenses, Hotel Due, Total Profit.',
        },
        {
          text: 'Review booking intensity: Total Bookings / Cancelled, Booked Room / Total Room, Occupancy Percentage, Total Customers.',
        },
        {
          text: 'Inspect charts for revenue vs expenses over time, payment method mix, booking status trends, and booking sources.',
        },
        {
          text: 'Check inventory charts for direct, online, and overbooking signals.',
        },
        {
          text: 'Drill into unusual spikes by opening Booking List, Payments, or POS orders for those dates.',
        },
        {
          text: 'Export PDF when you need a meeting pack. Confirm pop-ups are allowed so the download completes.',
        },
        {
          text: 'Share 2–3 actions with the team (rate change, staffing, channel mix) and track them next week on the same range pattern.',
        },
      ]),
    ],
    screens: [
      fieldTable([
        ['Start / end date', 'Yes', 'Filters all KPI cards and charts to the selected range'],
        ['PDF export', 'No', 'Downloads a multi-section performance snapshot'],
        ['KPI metric cards', '—', 'Revenue, expenses, profit, bookings, occupancy, customers'],
        ['Trend / pie charts', '—', 'Payment methods, sources, status, inventory'],
      ]),
      bullets(
        [
          'Total Revenue — room and related sales aggregated for the range',
          'Hotel expenses / Hotel Due — purchases and outstanding checkout balance for margin context',
          'Total Profit — revenue minus expenses as computed for the dashboard',
          'Occupancy Percentage — booked rooms vs hotel inventory capacity',
          'Booking Sources — direct vs channel mix for distribution decisions',
        ],
        'KPI meanings (property)'
      ),
    ],
    workflow: [
      workflow('Weekly manager review', [
        'Open Performance',
        'Set comparable date range',
        'Note ADR/occupancy/profit',
        'Inspect sources & payments',
        'Investigate spikes in lists/reports',
        'Adjust rates or staffing',
        'Export PDF / brief team',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Figures aggregate operational data for the selected hotel: bookings, payments, expenses, and due metrics exposed by the statistics API. They are not a substitute for statutory accounting exports when your finance team needs ledgers.',
        'Timing matters. Same-day POS postings, late checkouts, and cancellations can move MTD numbers during the day. For owner meetings, agree whether you freeze a PDF at a fixed time.',
        'Occupancy uses booked rooms against hotel total rooms/inventory. Overbooking signals in inventory charts highlight sold volume beyond capacity logic used by the dashboard — investigate before assuming the chart is “wrong”.',
        'Organisation Dashboard uses similar widgets but rolls up one or more hotels you select on Landing. Do not mix the two when presenting group vs property results.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Pair with Reports',
        'Use Reports Hub when you need exportable registers, not just charts.'
      ),
      callout(
        'tip',
        'Source mix',
        'If OTA share grows while ADR falls, review Distribution allotments and Manage Rate before blaming FO.'
      ),
      callout(
        'note',
        'Hotel due',
        'Hotel Due includes outstanding balances on checked-out and partial-checkout bookings in the selected period.'
      ),
    ],
    bestPractices: [
      bullets([
        'Review at the same time each week for comparable trends.',
        'Export PDF after night close when possible for cleaner MTD snapshots.',
        'Annotate one page of actions; do not circulate charts without decisions.',
        'Reconcile surprising revenue with Payments and Cash Counter before changing strategy.',
        'Keep Performance access limited to managers — FO should live on Home Dashboard.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Reading occupancy without confirming the date filter.',
        'Presenting property Performance as if it were Organisation rollup.',
        'Chasing a same-day spike without waiting for checkout/POS settlement.',
        'Ignoring cancelled booking ratio when celebrating gross booking counts.',
        'Using charts alone for tax/GST filing instead of finance reports.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'Fix'],
        [
          [
            'Zeros everywhere',
            'No postings in range or permission blocked',
            'Widen dates; confirm bookings/payments exist; verify role access',
          ],
          [
            'PDF fails',
            'Pop-up blocker or heavy range',
            'Allow pop-ups; shorten date range; retry',
          ],
          [
            'Numbers disagree with Reports',
            'Different filters/status definitions',
            'Match date range; compare like-for-like reports; ask finance for definition',
          ],
          [
            'Restaurant charts empty',
            'POS unused or no sales in range',
            'Confirm POS orders exist; otherwise ignore restaurant widgets',
          ],
          [
            'Cannot find menu item',
            'Missing analytics permission',
            'Ask admin for view_reports / performance access',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Is this Organisation Dashboard?',
          a: 'No. Organisation Dashboard on Landing rolls up multiple hotels. Performance Dashboard is per property.',
        },
        {
          q: 'Can I edit bookings here?',
          a: 'No. This is analytics only. Use Booking List / Detail for edits.',
        },
        {
          q: 'Why did profit change after lunch?',
          a: 'Expenses or revenue posted later in the day (POS, purchases, late payments) update aggregates.',
        },
        {
          q: 'Which permission do I need?',
          a: 'Typically report/analytics access such as view_reports. Exact menu visibility follows your role and package.',
        },
        {
          q: 'How do I share with ownership?',
          a: 'Use PDF export, or screenshot KPI cards after freezing the agreed date range.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'booking-dashboard',
    title: 'Booking Dashboard',
    moduleId: 'dashboard',
    summary: 'Booking KPIs and charts with PDF export for reservation performance.',
    keywords: ['booking dashboard', 'booking kpi', 'pdf export'],
    pathHint: 'Hotel → Booking Dashboard',
    related: ['dashboard-home', 'booking-list', 'reports-hub'],
    overview: [
      ...paragraphs(
        'Booking Dashboard focuses on reservation performance for a single hotel: booking counts, cancellations, status trends, and related KPI tiles with charts. Front-office managers use it to watch pick-up health, cancellation spikes, and stay patterns.',
        'It complements Home Dashboard (operations) and Performance Dashboard (broader revenue/expense analytics). When you need a shareable booking snapshot, export PDF from this page.',
        'Default range is commonly the current month; changing dates reloads booking statistics. Navigation links may jump to Payments, Customers, Reports, or Manage Rate when your permissions allow.',
        'Example: after a long weekend, the manager opens Booking Dashboard, sees cancellations elevated mid-week, exports PDF for the owner, then uses Booking List filters to call at-risk arrivals and tighten guarantee policy.',
        'This screen does not replace Check-in or Room Allocation — it will not tell you which room key to hand over at 2 PM.'
      ),
    ],
    beforeYouBegin: [
      permissionTable([
        [
          'Booking / report view access',
          'You need permission to open booking analytics for the hotel',
        ],
        ['Hotel context', 'Open the correct property first'],
      ]),
      bullets(
        [
          'Booking activity in the selected hotel and date range',
          'Pop-ups allowed if you plan to export PDF',
          'Clarify whether you are reviewing arrival dates vs booking-created dates with your manager',
        ],
        'Prerequisites'
      ),
      callout(
        'note',
        'Operational vs analytical',
        'For room assignment today, use Home Dashboard / Check-in — not this screen.'
      ),
    ],
    steps: [
      steps([
        {
          text: 'Open Booking Dashboard from the hotel menu.',
        },
        {
          text: 'Set start and end dates for the period you want to review (MTD, last week, event dates).',
        },
        {
          text: 'Wait for KPI tiles and charts to load; note any error alert if statistics fail.',
        },
        {
          text: 'Review booking volume, cancellations, and status/trend charts for anomalies.',
        },
        {
          text: 'Use header nav links (Payments, Customers, Reports, Manage Rate) when you need to act on what the charts suggest.',
        },
        {
          text: 'Jump to Booking List to investigate specific reservations behind a spike.',
        },
        {
          text: 'Export PDF for ownership or shift briefing packs when required.',
        },
        {
          text: 'Document one follow-up (guarantee calls, rate change, channel issue) so the chart review produces action.',
        },
      ]),
    ],
    screens: [
      fieldTable([
        ['Date range controls', 'Yes', 'Reload booking statistics for the selected window'],
        ['KPI tiles', '—', 'High-level booking performance counters'],
        ['Charts', '—', 'Trends and breakdowns for booking health'],
        ['PDF export', 'No', 'Shareable snapshot of the booking dashboard'],
        ['Nav links', 'Permission-based', 'Shortcuts to Payments, Customers, Reports, Manage Rate'],
      ]),
      bullets(
        [
          'Loading skeletons / alerts — indicate fetch state or API errors',
          'Theme-aware cards — match light/dark hotel UI',
          'Report header — titles the analytics pack for PDF capture',
        ],
        'UI notes'
      ),
    ],
    workflow: [
      workflow('Daily booking health check', [
        'Open Booking Dashboard',
        'Set today’s or MTD range',
        'Check cancellations / no-show signals',
        'Follow up at-risk arrivals in Booking List',
        'Export PDF if required',
        'Brief FO on policy tweaks',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Metrics are derived from booking statistics for the selected hotel and date range — counts and status movements such as cancellations, not live room-assignment detail.',
        'PDF export renders the on-screen report (and tabular summaries where implemented) into a downloadable file named for the range. Browser pop-up blockers commonly interrupt the download.',
        'Because definitions can differ slightly from Finance or Performance widgets, always compare like-for-like date ranges when reconciling “why don’t these totals match?”'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Cancel spike?',
        'Check whether a channel outage or payment failure caused mass cancellations before blaming front desk.'
      ),
      callout(
        'tip',
        'Pair with Home Dashboard',
        'Use Booking Dashboard for patterns; use Home Dashboard for today’s arrivals still needing check-in.'
      ),
      callout(
        'note',
        'PDF naming',
        'Keep exported files named with hotel + date range so owners do not compare mismatched packs.'
      ),
    ],
    bestPractices: [
      bullets([
        'Export weekly PDFs for ownership packs with a consistent weekday cutoff.',
        'Investigate cancellation spikes the same day — do not wait for month end.',
        'Train supervisors to read charts, then act in Booking List.',
        'Align date-range conventions across managers (arrival-based vs created-based) for meetings.',
        'Limit edit rights discussion here — this page is read-only analytics.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Comparing PDFs that used different date filters.',
        'Trying to assign rooms or edit folios from this screen.',
        'Ignoring cancelled ratio while celebrating raw booking counts.',
        'Mixing Booking Dashboard conclusions with Organisation Dashboard totals.',
        'Leaving pop-ups blocked and assuming export is “broken”.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'Fix'],
        [
          [
            'PDF fails',
            'Pop-up blocker or large range',
            'Allow pop-ups; reduce date range; retry',
          ],
          [
            'Empty KPIs',
            'No bookings in range or fetch error',
            'Widen dates; refresh; check error alert',
          ],
          [
            'Totals disagree with Booking List',
            'Filter/status mismatch',
            'Match dates and statuses; recount with same filters',
          ],
          [
            'Menu missing',
            'Permission or package',
            'Ask admin for booking analytics / report access',
          ],
          [
            'Nav link missing',
            'Related module permission absent',
            'Expected — open module from main menu if allowed',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Can I edit bookings here?',
          a: 'No. Use Booking List / Detail to edit.',
        },
        {
          q: 'Is this the same as Home Dashboard?',
          a: 'No. Home Dashboard is the live calendar and tasks board. Booking Dashboard is KPI analytics.',
        },
        {
          q: 'When should I use Performance instead?',
          a: 'When you need revenue, expenses, profit, occupancy, and restaurant analytics together — not only booking counters.',
        },
        {
          q: 'Can front desk staff open this?',
          a: 'Only if their role includes the analytics permission. Many hotels keep it manager-only.',
        },
        {
          q: 'Do OTA bookings count?',
          a: 'Yes — once stored as PMS bookings they contribute to booking statistics for the hotel.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'tasks-view',
    title: 'Pending Tasks',
    moduleId: 'dashboard',
    summary: 'Track and clear operational pending tasks for the property (including mobile Tasks view).',
    keywords: ['tasks', 'pending', 'mobile tasks', 'follow up'],
    pathHint: 'Dashboard or mobile → Tasks (?view=tasks)',
    related: ['dashboard-home', 'check-in', 'housekeeping-overview'],
    overview: [
      ...paragraphs(
        'Pending Tasks highlights work that still needs a human action for the selected hotel: arrivals to check in, departures to check out, no-show follow-ups, housekeeping cleanings, and maintenance items that affect sellability.',
        'On desktop, tasks appear as a rail or panel beside the Home Dashboard calendar. On mobile, a dedicated Tasks view keeps floor staff focused when they are away from the full grid.',
        'Tasks are reminders derived from system state — not a separate to-do app. Completing check-in, checkout, or a cleaning job is what clears the item.',
        'Example: morning shift opens Tasks, processes overdue checkouts first (rooms needed for arrivals), then check-ins with clean rooms, then asks HK to finish pending cleanings still blocking assignment.',
        'Benefits: fewer forgotten arrivals, faster room turnover visibility for FO, and cleaner handovers because the next shift sees what is still open.'
      ),
    ],
    beforeYouBegin: [
      permissionTable([
        ['Hotel open', 'Tasks always belong to the selected property context'],
        [
          'Operational access',
          'You need rights to act on bookings and/or housekeeping items the tasks point to',
        ],
      ]),
      bullets(
        [
          'Know today’s date and which tasks are FO-owned vs HK-owned',
          'Agree escalation for overdue checkouts and blocked dirty rooms',
          'On mobile, use the Tasks view when you cannot see the full calendar',
        ],
        'Prerequisites'
      ),
      callout(
        'warning',
        'Do not “wish away” tasks',
        'If a task remains, the underlying booking or HK status is usually still open. Finish the real workflow.'
      ),
    ],
    steps: [
      steps([
        {
          text: 'Open Dashboard (desktop rail/panel) or mobile Tasks view for the hotel.',
        },
        {
          text: 'Note the total count and scan sections: checkout, check-in, no-show, housekeeping cleanings, maintenance.',
        },
        {
          text: 'Prioritise overdue checkouts and dirty rooms that block today’s arrivals.',
        },
        {
          text: 'Open a check-in task and complete Check-in (with a clean assignable room).',
        },
        {
          text: 'Open a checkout task and complete settlement / checkout so the room can turn over.',
        },
        {
          text: 'For housekeeping tasks, coordinate with HK — complete or progress the cleaning/maintenance in Housekeeping.',
        },
        {
          text: 'Handle no-show candidates per policy (call guest, extend hold, or mark no-show) so inventory frees correctly.',
        },
        {
          text: 'Refresh Tasks after a burst of work. Confirm counts drop as statuses change.',
        },
        {
          text: 'At handover, verbally own any lingering tasks (“two late checkouts, one VIP arrival”) so the next shift is not surprised.',
        },
      ]),
    ],
    screens: [
      bullets(
        [
          'Task list / sections — grouped operational follow-ups',
          'Task cards — guest/room subtitle, meta date/status, deep link into the module',
          'Desktop rail — collapsible beside the calendar; remembers collapsed preference',
          'Mobile Tasks tab — floor-focused list without the full grid',
          'Refresh behaviour — reloads from pending-tasks APIs / cache',
        ],
        'UI elements'
      ),
      table(
        ['Section (typical)', 'Meaning', 'Clear by'],
        [
          ['Checkout', 'In-house departure due/overdue', 'Complete Checkout & payment'],
          ['Check-in', 'Arrival still not fully in-house', 'Complete Check-in'],
          ['No-show', 'Reserved guest did not arrive as expected', 'Follow policy / update booking status'],
          ['Housekeeping', 'Pending/in-progress cleaning', 'Complete cleaning job / update HK'],
          ['Maintenance', 'Repair blocking readiness', 'Close maintenance when fixed'],
        ],
        'Task sections'
      ),
    ],
    workflow: [
      workflow('Clear morning tasks', [
        'Open Tasks',
        'Prioritise overdue checkouts',
        'Unblock dirty arrival rooms via HK',
        'Process check-ins',
        'Resolve no-show candidates',
        'Recheck list',
        'Handover leftovers',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Pending Tasks aggregate several operational feeds (checkout list including pending lookback, today’s check-ins in confirmed/reserved/pending/partial states, no-shows, cleanings, maintenance). The bundle can be cached briefly on Dashboard for speed and refreshed after data changes.',
        'Tasks reflect system state. Completing the underlying workflow removes or updates the task — there is usually nothing to delete separately. If a task “will not clear”, the booking status or HK job is still open.',
        'FO and HK share some visibility: a pending cleaning task on the FO rail is a signal not to assign that room yet, even though detailed HK work lives under Housekeeping.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Shift start ritual',
        'Clear Tasks before personal breaks so guests are not waiting.'
      ),
      callout(
        'tip',
        'Dirty room + arrival',
        'Never check in to a room that still has a pending cleaning task — confirm Room Status first.'
      ),
      callout(
        'note',
        'Mobile focus',
        'When roaming floors, use Tasks view to chase cleanings and then return to Desktop calendar for selling.'
      ),
    ],
    bestPractices: [
      bullets([
        'Assign verbal owners for lingering tasks during every handover.',
        'Process checkouts that free inventory before non-critical admin work.',
        'Align no-show timing with hotel policy and night audit habits.',
        'Refresh after batch check-ins so counts stay trustworthy.',
        'Use Housekeeping boards for deep HK control; use Tasks as the FO alert layer.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Marking mental notes done without updating Roomnexa statuses.',
        'Ignoring maintenance tasks until a guest is standing at the desk.',
        'Checking in before HK finishes a pending cleaning.',
        'Leaving no-show candidates open all day and blocking inventory.',
        'Assuming Tasks replace the Housekeeping module entirely.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'Fix'],
        [
          [
            'Task will not clear',
            'Underlying status still open',
            'Finish check-in/HK/payment status in the source module',
          ],
          [
            'Task missing for a known arrival',
            'Status already checked in or date mismatch',
            'Verify booking status and stay dates; refresh Tasks',
          ],
          [
            'Stale tasks after completing work',
            'Cached bundle not refreshed',
            'Refresh Dashboard/Tasks; retry after a moment',
          ],
          [
            'HK task with no permission to act',
            'FO role cannot manage housekeeping',
            'Notify HK supervisor; FO should not force-assign dirty rooms',
          ],
          [
            'Empty list on mobile',
            'Wrong hotel or truly clear queue',
            'Confirm hotel context; verify on desktop rail',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Are Tasks the same as Housekeeping jobs?',
          a: 'No. HK cleanings live under Housekeeping. Tasks are cross-operational reminders that may include some HK items for FO visibility.',
        },
        {
          q: 'Can I delete a task manually?',
          a: 'Generally no — complete the related workflow so the task updates automatically.',
        },
        {
          q: 'Why do checkout tasks include older dates?',
          a: 'Pending checkout lookback can surface overdue in-house departures so rooms are not forgotten.',
        },
        {
          q: 'Do tasks create notifications outside Roomnexa?',
          a: 'They are in-app operational lists. External WhatsApp/email alerts depend on separate Communications setup.',
        },
        {
          q: 'Who should clear check-in tasks?',
          a: 'Front office with booking permissions. HK clears cleaning/maintenance tasks.',
        },
      ]),
    ],
  }),
];
